import { AxiosRequestConfig } from 'axios';
import { IVacancyCvSearchParams } from 'data/interfaces/Common';
import { ProfileType } from 'data/interfaces/IRegistration';
import { IVacancy, IVacancyCreate } from 'data/interfaces/IVacancy';
import { IPagination } from 'types/types';
import Converter from 'utils/converter';
import request from 'utils/request';

export default class VacancyRepository {
  static async createVacancy(data: IVacancyCreate): Promise<any | null> {
    const res = await request({
      method: 'post',
      url: '/api/vacancy',
      data,
    });
    if (res?.err) {
      return null;
    }
    return res;
  }

  static async getUserVacancies(token?: string, profile?: ProfileType): Promise<IVacancy[] | null> {
    const res = await request({
      method: 'get',
      url: '/api/vacancy/currentUser',
      token,
      profile,
      data: {
        sort: 'updatedAt,DESC',
      },
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async removeFromBlackList(vacancyId: number): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/vacancy/${vacancyId}/removeFromBlackList`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async editVacancyById(data: IVacancyCreate, id: number): Promise<any | null> {
    const fieldsToKeep: (keyof IVacancyCreate)[] = [
      'name',
      'description',
      'experience',
      'contactName',
      'company',
      'compensation',
      'phone',
      'email',
      'telegramNickname',
      'salaryMax',
      'salaryMin',
      'cityId',
      'countryId',
      'currency',
      'skillsIds',
      'sourcesIds',
      'categoriesIds',
      'schedule',
      'employment',
      'languageKnowledges',
      'showContactName',
      'verticalsIds',
      'geosIds',
      'completedSteps',
      'showEmail',
      'showPhone',
      'showTelegramNickname',
      'isRemote',
    ];

    const filteredData = Converter.pickFieldsFromObject(data, fieldsToKeep);

    const res = await request({
      method: 'patch',
      url: `/api/vacancy/${id}`,
      data: filteredData,
    });

    if (res.err) return null;
    return res;
  }

  /*
  передаю токен и профайл для удобного редактирования вакансии -
  поля сразу заполнены, пользователь не видит пустых полей, если вакансия еще не загрузилась
  */
  static async getVacancyById(
    id: number,
    profile?: ProfileType,
    token?: string,
  ): Promise<IVacancy | null> {
    if (!id || id === 0) {
      return null;
    }
    const res = await request({
      method: 'get',
      url: `/api/vacancy/${id}`,
      profile,
      token,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getSavedFromCurrentProfile(
    page?: number,
    limit?: number,
  ): Promise<IPagination<IVacancy> | null> {
    const res = await request({
      method: 'get',
      url: '/api/vacancy',
      data: {
        savedByCurrentProfile: true,
        page,
        limit,
      },
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async addVcToSaved(id: any, config?: AxiosRequestConfig): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/vacancy/${id}/save`,
      config,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async removeVcFromSaved(id: number, config?: AxiosRequestConfig): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/vacancy/${id}/removeFromSaved`,
      config,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async removeVacancy(id: number): Promise<any | null> {
    const res = await request({
      method: 'delete',
      url: `/api/vacancy/${id}`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getBlacklistedVacancies(): Promise<IPagination<IVacancy> | null> {
    const res = await request({
      method: 'get',
      url: '/api/vacancy?blackListedByCurrentProfile=true',
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async searchVacancies(
    data: IVacancyCvSearchParams,
    page?: number,
    limit: number = 40,
  ): Promise<IPagination<IVacancy> | null> {
    const dataToSend: any = { ...data, page, limit, sort: 'createdAt,DESC' };
    if (data?.employment && data.employment.length > 0)
      dataToSend.employment = data.employment.join(',');

    if (data?.sources && data.sources.length > 0) dataToSend.sources = data.sources.join(',');

    if (data?.categories && data.categories.length > 0)
      dataToSend.categories = data.categories.join(',');
    if (data?.experience && data.experience.length > 0)
      dataToSend.experience = data.experience.join(',');
    if (data?.cities && data.cities.length > 0) dataToSend.cities = data.cities.join(',');
    if (data?.countries && data.countries.length > 0)
      dataToSend.countries = data.countries.join(',');
    if (data?.schedule && data.schedule.length > 0) dataToSend.schedule = data.schedule.join(',');
    if (data?.languageKnowledges && data.languageKnowledges.length > 0)
      dataToSend.languageKnowledges = JSON.stringify(
        data.languageKnowledges?.map((el) => {
          return { language: el.language, levels: [el.level] };
        }),
      );
    const res = await request({
      method: 'get',
      url: '/api/vacancy',
      data: dataToSend,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async suggestPosition(data: { search: string }): Promise<{ name: string }[] | null> {
    const res = await request({
      method: 'get',
      url: '/api/vacancy/suggest',
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async addToBlackList(vacancyId: number): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/vacancy/${vacancyId}/blackList`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async publish(
    vacancyId: number,
    token: string,
    profile: ProfileType,
    productId: number,
    schedulePublishAt?: string,
    data?: any,
  ): Promise<IVacancy | null> {
    const params = {
      productId: productId.toString(),
      schedulePublishAt,
    };

    if (!params.schedulePublishAt) {
      delete params.schedulePublishAt;
    }

    const res = await request({
      method: 'put',
      url: `/api/vacancy/${vacancyId}/publish?${new URLSearchParams(params).toString()}`,
      profile,
      token,
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async archive(
    vacancyId: number,
    token: string,
    profile: ProfileType,
  ): Promise<{ status: boolean } | null> {
    const res = await request({
      method: 'delete',
      url: `/api/vacancy/archive/${vacancyId}`,
      profile,
      token,
    });

    return res;
  }
}
